import React, { useState, useEffect, useRef } from 'react';

// RH-inspired theme palettes
const themes = {
  'RH Linen': {
    bg: '#f5f2ed',
    bgElevated: '#faf8f5',
    surface: '#ffffff',
    border: '#e8e5df',
    text: '#3d3d3d',
    textSecondary: '#7a7772',
    textTertiary: '#9d9a94',
    accent: '#c8977c',
    accentSubtle: '#e8d5c8',
    shadow: 'rgba(61, 61, 61, 0.06)',
  },
  'Dark Oak': {
    bg: '#2d2822',
    bgElevated: '#342f29',
    surface: '#3d3731',
    border: '#4a4539',
    text: '#e8e5df',
    textSecondary: '#b8b3ab',
    textTertiary: '#8a857d',
    accent: '#9d8574',
    accentSubtle: '#574d42',
    shadow: 'rgba(0, 0, 0, 0.2)',
  },
  'Stonewash': {
    bg: '#e8eaeb',
    bgElevated: '#f2f3f4',
    surface: '#ffffff',
    border: '#d4d6d8',
    text: '#2d2d2d',
    textSecondary: '#6a6a6a',
    textTertiary: '#9a9a9a',
    accent: '#7a8087',
    accentSubtle: '#c8ccd0',
    shadow: 'rgba(45, 45, 45, 0.06)',
  },
  'Alabaster': {
    bg: '#fdfcfb',
    bgElevated: '#ffffff',
    surface: '#ffffff',
    border: '#f0eeeb',
    text: '#1a1a1a',
    textSecondary: '#5a5a5a',
    textTertiary: '#8a8a8a',
    accent: '#b89f8a',
    accentSubtle: '#e8dfd4',
    shadow: 'rgba(26, 26, 26, 0.04)',
  },
  'Driftwood': {
    bg: '#e5e3de',
    bgElevated: '#edeae5',
    surface: '#f5f3ef',
    border: '#d0cec9',
    text: '#3a3935',
    textSecondary: '#6d6a64',
    textTertiary: '#9a9791',
    accent: '#8b8378',
    accentSubtle: '#c7c2b8',
    shadow: 'rgba(58, 57, 53, 0.07)',
  },
};

const DEFAULT_SPHERE_COLORS = [
  '#c8977c', '#7a8087', '#b89f8a', '#8b8378', '#9d8574',
  '#a89080', '#8e9196', '#b0a090', '#7d8285', '#9a8875'
];

export default function FocusCommitmentTracker() {
  const [currentTheme, setCurrentTheme] = useState(() => {
    return localStorage.getItem('focusTrackerTheme') || 'RH Linen';
  });
  
  const theme = themes[currentTheme];

  const [sessions, setSessions] = useState([]);
  const [coherenceSessions, setCoherenceSessions] = useState([]);
  const [workSpheres, setWorkSpheres] = useState(() => {
    const saved = localStorage.getItem('workSpheres');
    return saved ? JSON.parse(saved) : [];
  });
  const [tasks, setTasks] = useState(() => {
    const saved = localStorage.getItem('activeTasks');
    return saved ? JSON.parse(saved) : [];
  });
  const [activeCommitment, setActiveCommitment] = useState(null);
  const [activeCoherence, setActiveCoherence] = useState(null);
  const [commitment, setCommitment] = useState('');
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskSphere, setNewTaskSphere] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [coherenceElapsed, setCoherenceElapsed] = useState(0);
  const [view, setView] = useState('active');
  const [historySubView, setHistorySubView] = useState('sessions');
  const [breathPhase, setBreathPhase] = useState('inhale');
  const [showInstructions, setShowInstructions] = useState(true);
  const [showContextSwitching, setShowContextSwitching] = useState(false);
  const [pauseNotes, setPauseNotes] = useState({});
  const [newSphereName, setNewSphereName] = useState('');
  const [newSphereColor, setNewSphereColor] = useState(DEFAULT_SPHERE_COLORS[0]);
  const [selectedTechnique, setSelectedTechnique] = useState(null);
  const [coherenceEmotion, setCoherenceEmotion] = useState('');
  const [totalCoherenceTime, setTotalCoherenceTime] = useState(0); // Track total HeartMath time during commitment
  const [editingSphere, setEditingSphere] = useState(null); // Track which sphere is being edited
  const [editSphereName, setEditSphereName] = useState('');
  const [editSphereColor, setEditSphereColor] = useState('');
  
  // Onboarding state
  const [sessionCount, setSessionCount] = useState(() => {
    return parseInt(localStorage.getItem('sessionCount') || '0');
  });
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [onboardingStep, setOnboardingStep] = useState('map');
  
  const breathTimerRef = useRef(null);

  const techniqueInstructions = {
    'Quick Coherence': 'Focus your attention on your heart area. Breathe slowly and deeply. As you breathe, recall a positive feeling or memory.',
    'Heart Lock-In': 'Focus on your heart and breathe slowly. Activate a positive feeling (appreciation, care, compassion). Maintain this feeling for the duration.',
    'Inner Ease': 'Focus on your heart while breathing slowly. Imagine breathing ease and calm into any tense areas. Let go of mental and emotional tension.'
  };

  useEffect(() => {
    localStorage.setItem('focusTrackerTheme', currentTheme);
  }, [currentTheme]);

  useEffect(() => {
    localStorage.setItem('workSpheres', JSON.stringify(workSpheres));
  }, [workSpheres]);

  useEffect(() => {
    const saved = localStorage.getItem('focusSessions');
    if (saved) {
      setSessions(JSON.parse(saved));
    }
    const savedCoherence = localStorage.getItem('coherenceSessions');
    if (savedCoherence) {
      setCoherenceSessions(JSON.parse(savedCoherence));
    }
    const active = localStorage.getItem('activeCommitment');
    if (active) {
      setActiveCommitment(JSON.parse(active));
    }
  }, []);

  useEffect(() => {
    if (sessions.length > 0) {
      localStorage.setItem('focusSessions', JSON.stringify(sessions));
    }
  }, [sessions]);

  useEffect(() => {
    if (coherenceSessions.length > 0) {
      localStorage.setItem('coherenceSessions', JSON.stringify(coherenceSessions));
    }
  }, [coherenceSessions]);

  useEffect(() => {
    localStorage.setItem('activeTasks', JSON.stringify(tasks));
  }, [tasks]);

  useEffect(() => {
    const hasRunningTasks = tasks.some(t => t.status === 'running');
    if (!hasRunningTasks) return;
    
    const interval = setInterval(() => {
      setTasks(prev => [...prev]);
    }, 1000);

    return () => clearInterval(interval);
  }, [tasks.some(t => t.status === 'running')]);

  useEffect(() => {
    if (!activeCommitment) return;
    
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - activeCommitment.startTime) / 1000);
      setElapsedTime(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [activeCommitment]);

  useEffect(() => {
    if (!activeCoherence) return;
    
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - activeCoherence.startTime) / 1000);
      setCoherenceElapsed(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [activeCoherence]);

  useEffect(() => {
    if (!activeCoherence) {
      if (breathTimerRef.current) {
        clearInterval(breathTimerRef.current);
        breathTimerRef.current = null;
      }
      return;
    }

    const inhaleTime = 5000;
    setBreathPhase('inhale');
    
    breathTimerRef.current = setInterval(() => {
      setBreathPhase(prev => prev === 'inhale' ? 'exhale' : 'inhale');
    }, inhaleTime);

    return () => {
      if (breathTimerRef.current) {
        clearInterval(breathTimerRef.current);
        breathTimerRef.current = null;
      }
    };
  }, [activeCoherence]);

  const addWorkSphere = () => {
    if (!newSphereName.trim()) return;
    
    const sphere = {
      id: Date.now(),
      name: newSphereName.trim(),
      color: newSphereColor
    };
    
    setWorkSpheres([...workSpheres, sphere]);
    setNewSphereName('');
    setNewSphereColor(DEFAULT_SPHERE_COLORS[0]);
  };

  const updateWorkSphere = (sphereId) => {
    if (!editSphereName.trim()) return;
    
    setWorkSpheres(workSpheres.map(s => 
      s.id === sphereId 
        ? { ...s, name: editSphereName.trim(), color: editSphereColor }
        : s
    ));
    setEditingSphere(null);
    setEditSphereName('');
    setEditSphereColor('');
  };

  const deleteWorkSphere = (sphereId) => {
    // Check if any tasks use this sphere
    const tasksUsingThisSphere = tasks.filter(t => t.sphereId === sphereId);
    if (tasksUsingThisSphere.length > 0) {
      alert(`Cannot delete this sphere. ${tasksUsingThisSphere.length} task(s) are using it.`);
      return;
    }
    
    setWorkSpheres(workSpheres.filter(s => s.id !== sphereId));
  };

  const addTask = () => {
    if (!newTaskName.trim() || !newTaskSphere) return;
    
    const task = {
      id: Date.now(),
      name: newTaskName.trim(),
      sphereId: newTaskSphere,
      totalTime: 0,
      currentSessionStart: null,
      sessions: [],
      checkIns: [],
      status: 'paused',
      createdAt: Date.now(),
      completedAt: null
    };
    
    setTasks([...tasks, task]);
    setNewTaskName('');
    setNewTaskSphere('');
  };

  const addTaskCheckIn = (taskId, focused) => {
    setTasks(tasks.map(t => 
      t.id === taskId 
        ? { ...t, checkIns: [...t.checkIns, { time: Date.now(), focused }] }
        : t
    ));
  };

  const startTask = (taskId) => {
    setTasks(tasks.map(t => 
      t.id === taskId 
        ? { ...t, status: 'running', currentSessionStart: Date.now() }
        : t
    ));
  };

  const pauseTask = (taskId) => {
    const note = pauseNotes[taskId] || '';
    
    setTasks(tasks.map(t => {
      if (t.id === taskId && t.status === 'running') {
        const sessionTime = t.currentSessionStart ? (Date.now() - t.currentSessionStart) : 0;
        return {
          ...t,
          status: 'paused',
          totalTime: t.totalTime + sessionTime,
          sessions: [...t.sessions, { 
            start: t.currentSessionStart, 
            end: Date.now(), 
            duration: Math.floor(sessionTime / 1000),
            note: note
          }],
          currentSessionStart: null
        };
      }
      return t;
    }));
    
    setPauseNotes(prev => ({ ...prev, [taskId]: '' }));
  };

  const getCurrentTaskTime = (task) => {
    if (task.status === 'running' && task.currentSessionStart) {
      const currentSession = Math.floor((Date.now() - task.currentSessionStart) / 1000);
      return task.totalTime + currentSession;
    }
    return task.totalTime;
  };

  const deleteTask = (taskId) => {
    setTasks(tasks.filter(t => t.id !== taskId));
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const startCommitment = () => {
    if (!commitment.trim()) return;
    
    const newCommitment = {
      id: Date.now(),
      commitment: commitment.trim(),
      startTime: Date.now(),
      taskSwitches: [],
      completed: false
    };
    
    setActiveCommitment(newCommitment);
    localStorage.setItem('activeCommitment', JSON.stringify(newCommitment));
    setCommitment('');
    
    if (onboardingStep === 'resources' && sessionCount < 10) {
      setShowOnboarding(false);
    }
  };

  const endCommitment = () => {
    if (!activeCommitment) return;
    
    // First, pause any running tasks and get the updated task list
    const updatedTasks = tasks.map(task => {
      if (task.status === 'running' && task.currentSessionStart) {
        const sessionTime = task.currentSessionStart ? (Date.now() - task.currentSessionStart) : 0;
        return {
          ...task,
          status: 'paused',
          totalTime: task.totalTime + sessionTime,
          currentSessionStart: null,
          sessions: [...task.sessions, {
            start: task.currentSessionStart,
            end: Date.now(),
            duration: sessionTime,
            note: ''
          }]
        };
      }
      return task;
    });
    
    // Update the tasks state with paused tasks
    setTasks(updatedTasks);
    
    const taskSwitches = [];
    updatedTasks.forEach(task => {
      task.sessions.forEach(session => {
        if (session.start >= activeCommitment.startTime) {
          taskSwitches.push({
            taskId: task.id,
            taskName: task.name,
            sphereId: task.sphereId,
            start: session.start,
            end: session.end,
            duration: session.duration,
            note: session.note
          });
        }
      });
    });
    
    taskSwitches.sort((a, b) => a.start - b.start);
    
    const completedCommitment = {
      ...activeCommitment,
      endTime: Date.now(),
      completed: true,
      duration: elapsedTime,
      taskSwitches,
      coherenceTime: totalCoherenceTime // Save total HeartMath time
    };
    
    setSessions([completedCommitment, ...sessions]);
    setActiveCommitment(null);
    localStorage.removeItem('activeCommitment');
    setElapsedTime(0);
    setTotalCoherenceTime(0); // Reset coherence time counter
    
    const newCount = sessionCount + 1;
    setSessionCount(newCount);
    localStorage.setItem('sessionCount', newCount.toString());
  };

  const startCoherence = (technique, emotion) => {
    const session = {
      technique,
      emotion,
      startTime: Date.now()
    };
    setActiveCoherence(session);
    setSelectedTechnique(null);
    setCoherenceEmotion('');
  };

  const endCoherence = () => {
    if (!activeCoherence) return;
    
    const completedCoherence = {
      ...activeCoherence,
      endTime: Date.now(),
      duration: coherenceElapsed
    };
    
    setCoherenceSessions([completedCoherence, ...coherenceSessions]);
    
    // Add to total coherence time for this commitment
    if (activeCommitment) {
      setTotalCoherenceTime(prev => prev + coherenceElapsed);
    }
    
    setActiveCoherence(null);
    setCoherenceElapsed(0);
    setBreathPhase('inhale');
  };

  const exportData = () => {
    const data = {
      sessions,
      coherenceSessions,
      tasks,
      workSpheres,
      exportedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `focus-tracker-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const importData = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (data.sessions) setSessions(data.sessions);
        if (data.coherenceSessions) setCoherenceSessions(data.coherenceSessions);
        if (data.tasks) setTasks(data.tasks);
        if (data.workSpheres) setWorkSpheres(data.workSpheres);
        alert('Data imported successfully!');
      } catch (error) {
        alert('Error importing data. Please check the file format.');
      }
    };
    reader.readAsText(file);
  };

  const getSphereById = (sphereId) => {
    return workSpheres.find(s => s.id === sphereId);
  };

  const activeTasks = tasks.filter(t => !t.completedAt);

  if (showOnboarding && onboardingStep === 'map') {
    return (
      <div style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        maxWidth: '400px',
        minHeight: '100vh',
        background: theme.bg,
        color: theme.text,
        padding: '32px 24px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Split-screen visual hint - simulated workspace */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(90deg, 
            ${theme.textTertiary}08 0%, 
            ${theme.textTertiary}05 40%, 
            transparent 40%, 
            transparent 60%,
            ${theme.accent}03 60%,
            ${theme.accent}08 100%)`,
          pointerEvents: 'none',
        }} />
        
        {/* Overlay to ensure readability */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(180deg, ${theme.bg}f5 0%, ${theme.bg}ee 100%)`,
          pointerEvents: 'none',
        }} />
        
        <div style={{ textAlign: 'center', marginBottom: '48px', position: 'relative', zIndex: 1 }}>
          {/* Logo */}
          <svg width="80" height="80" viewBox="0 0 80 80" style={{ margin: '0 auto 16px' }}>
            <circle cx="40" cy="40" r="16" fill="none" stroke={theme.accent} strokeWidth="2" opacity="0.3"/>
            <circle cx="40" cy="40" r="12" fill="none" stroke={theme.accent} strokeWidth="2" opacity="0.5"/>
            <circle cx="40" cy="40" r="8" fill="none" stroke={theme.accent} strokeWidth="2.5" opacity="0.7"/>
            <circle cx="40" cy="40" r="5" fill={theme.accent}/>
          </svg>
          
          <div style={{ fontSize: '24px', fontWeight: '300', marginBottom: '12px', letterSpacing: '1px' }}>
            COMPOSURE
          </div>
          <div style={{ fontSize: '13px', color: theme.textSecondary, lineHeight: '1.5' }}>
            Declare your intention. Track your focus. Build awareness.
          </div>
        </div>

        <div style={{ marginBottom: '32px', position: 'relative', zIndex: 1 }}>
          <div style={{
            padding: '20px',
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: '2px',
            marginBottom: '16px',
            boxShadow: `0 1px 3px ${theme.shadow}`,
          }}>
            <div style={{ fontSize: '11px', color: theme.accent, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '8px' }}>
              1. Start a Commitment
            </div>
            <div style={{ fontSize: '13px', color: theme.textSecondary, lineHeight: '1.5' }}>
              Write what you're working on. This creates a mental contract and closes the loop.
            </div>
          </div>

          <div style={{
            padding: '20px',
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: '2px',
            marginBottom: '16px',
            boxShadow: `0 1px 3px ${theme.shadow}`,
          }}>
            <div style={{ fontSize: '11px', color: theme.accent, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '8px' }}>
              2. Work on Tasks
            </div>
            <div style={{ fontSize: '13px', color: theme.textSecondary, lineHeight: '1.5' }}>
              Track time across related tasks. Strategic switching is supported, not punished.
            </div>
          </div>

          <div style={{
            padding: '20px',
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: '2px',
            marginBottom: '16px',
            boxShadow: `0 1px 3px ${theme.shadow}`,
          }}>
            <div style={{ fontSize: '11px', color: theme.accent, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '8px' }}>
              3. Take Coherence Breaks
            </div>
            <div style={{ fontSize: '13px', color: theme.textSecondary, lineHeight: '1.5' }}>
              Reset your nervous system with HeartMath breathing techniques.
            </div>
          </div>

          <div style={{
            padding: '20px',
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: '2px',
            boxShadow: `0 1px 3px ${theme.shadow}`,
          }}>
            <div style={{ fontSize: '11px', color: theme.accent, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '8px' }}>
              4. See Your Patterns
            </div>
            <div style={{ fontSize: '13px', color: theme.textSecondary, lineHeight: '1.5' }}>
              View switching patterns and discover how your attention flows across work spheres.
            </div>
          </div>
        </div>

        <div style={{
          padding: '16px',
          background: theme.accentSubtle,
          border: `1px solid ${theme.accent}`,
          borderRadius: '2px',
          marginBottom: '24px',
          fontSize: '12px',
          color: theme.text,
          lineHeight: '1.5',
          position: 'relative',
          zIndex: 1,
        }}>
          <strong style={{ display: 'block', marginBottom: '8px', fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            💡 Keep it visible
          </strong>
          Position Composure as a sidebar while you work. When it's in your peripheral vision, you're more likely to catch yourself before drifting off task.
        </div>

        <button
          onClick={() => {
            if (sessionCount < 10) {
              setOnboardingStep('resources');
            } else {
              setShowOnboarding(false);
            }
          }}
          style={{
            width: '100%',
            padding: '16px',
            background: theme.accent,
            border: 'none',
            borderRadius: '2px',
            color: theme.surface,
            fontSize: '12px',
            cursor: 'pointer',
            fontWeight: '500',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {sessionCount < 10 ? 'Continue' : 'Get Started'}
        </button>
      </div>
    );
  }

  if (showOnboarding && onboardingStep === 'resources') {
    return (
      <div style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        maxWidth: '400px',
        minHeight: '100vh',
        background: theme.bg,
        color: theme.text,
        padding: '24px',
      }}>
        <div style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          padding: '8px 16px',
          background: theme.accent,
          borderRadius: '2px',
          fontSize: '11px',
          color: theme.surface,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          cursor: 'pointer',
          zIndex: 1000,
        }}
        onClick={() => setShowOnboarding(false)}
        >
          Home
        </div>

        <div style={{ marginBottom: '32px', paddingTop: '24px', maxWidth: '600px' }}>
          <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '12px', color: theme.text }}>
            Why the Focus Tracker Works
          </div>
          <div style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, marginBottom: '12px' }}>
            The Focus Commitment Tracker leverages three evidence-based mechanisms:
          </div>
          <ul style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, paddingLeft: '20px', margin: 0 }}>
            <li style={{ marginBottom: '8px' }}>
              <strong style={{ color: theme.text, fontWeight: '500' }}>Implementation intentions:</strong> Writing down what you're working on creates a mental contract. Keeping it visible helps you redirect attention when you feel the impulse to switch—you can't unconsciously drift when the goal is staring back at you.
            </li>
            <li style={{ marginBottom: '8px' }}>
              <strong style={{ color: theme.text, fontWeight: '500' }}>Time awareness:</strong> Glancing at elapsed time creates a powerful anchor. Watching minutes accumulate can motivate you to stay on task or signal when it's time for a strategic break.
            </li>
            <li style={{ marginBottom: '8px' }}>
              <strong style={{ color: theme.text, fontWeight: '500' }}>Metacognitive check-ins:</strong> The "still focused?" and "got distracted" buttons build awareness of your attention patterns. Clicking reinforces progress when you're on track and helps you course-correct when you've drifted.
            </li>
          </ul>
        </div>

        <div style={{ paddingTop: '32px', borderTop: `1px solid ${theme.border}`, marginBottom: '32px', maxWidth: '600px' }}>
          <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '12px', color: theme.text }}>
            The Science of HeartMath Coherence
          </div>
          <div style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, marginBottom: '12px' }}>
            HeartMath techniques are built on decades of research into heart-brain coherence—a state where your heart rhythm, breathing, and nervous system synchronize. This state:
          </div>
          <ul style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, paddingLeft: '20px', marginBottom: '16px', margin: 0 }}>
            <li style={{ marginBottom: '8px' }}>
              <strong style={{ color: theme.text, fontWeight: '500' }}>Reduces cortisol:</strong> Even 5 minutes of heart-focused breathing can lower stress hormones and calm the fight-or-flight response.
            </li>
            <li style={{ marginBottom: '8px' }}>
              <strong style={{ color: theme.text, fontWeight: '500' }}>Improves focus:</strong> Coherence increases alpha brain waves associated with relaxed alertness, making it easier to concentrate without strain.
            </li>
            <li style={{ marginBottom: '8px' }}>
              <strong style={{ color: theme.text, fontWeight: '500' }}>Builds resilience:</strong> Regular practice strengthens your ability to self-regulate emotions, especially useful when work feels frustrating or overwhelming.
            </li>
          </ul>
          <div style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, marginBottom: '16px' }}>
            Instead of doomscrolling during wait times (like when Claude is generating a response), a 2-minute coherence session lets you reset your nervous system and return to work calmer and sharper.
          </div>
          <a
            href="https://www.heartmath.org"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: '14px',
              color: theme.accent,
              textDecoration: 'none',
              padding: '10px 16px',
              border: `1px solid ${theme.accent}`,
              borderRadius: '2px',
              marginBottom: '8px'
            }}
          >
            HeartMath Institute
            <span style={{ marginLeft: '6px' }}>→</span>
          </a>
          <div style={{ fontSize: '13px', color: theme.textSecondary }}>
            Explore research, techniques, and tools for heart-brain coherence.
          </div>
        </div>

        <div style={{ paddingTop: '32px', borderTop: `1px solid ${theme.border}`, maxWidth: '600px' }}>
          <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '12px', color: theme.text }}>
            Context-Switching for Portfolio Workers
          </div>
          <div style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, marginBottom: '12px' }}>
            Not all context-switching is bad. Research shows that self-initiated switches between related projects can actually boost creativity and problem-solving:
          </div>
          <ul style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, paddingLeft: '20px', marginBottom: '16px', margin: 0 }}>
            <li style={{ marginBottom: '8px' }}>
              <strong style={{ color: theme.text, fontWeight: '500' }}>Voluntary vs. forced switching:</strong> Self-initiated switches have lower cognitive costs than external interruptions (Arrington & Logan, 2005).
            </li>
            <li style={{ marginBottom: '8px' }}>
              <strong style={{ color: theme.text, fontWeight: '500' }}>Incubation effects:</strong> Stepping away from a stuck problem for 4-12 minutes improves solution rates, especially when switching to tasks using different cognitive resources.
            </li>
            <li style={{ marginBottom: '8px' }}>
              <strong style={{ color: theme.text, fontWeight: '500' }}>Cross-domain insights:</strong> Portfolio workers applying the same skill across different domains often experience productive "attention residue"—insights from Project A illuminating Project B.
            </li>
          </ul>
          
          <button
            onClick={() => setShowContextSwitching(!showContextSwitching)}
            style={{
              fontSize: '14px',
              color: theme.accent,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
              marginBottom: showContextSwitching ? '16px' : 0
            }}
          >
            {showContextSwitching ? 'Read less' : 'Read more'}
          </button>

          {showContextSwitching && (
            <div style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, marginTop: '16px' }}>
              <p style={{ marginBottom: '12px' }}>
                The "23-minute recovery time" and "2.26 task cascade" often cited in productivity research apply primarily to <strong style={{ color: theme.text }}>forced interruptions</strong> and <strong style={{ color: theme.text }}>unrelated tasks</strong>. The data looks different for portfolio workers:
              </p>
              
              <p style={{ marginBottom: '12px' }}>
                <strong style={{ color: theme.text, fontWeight: '500' }}>The cascade might be strategic:</strong> When researchers observed people visiting "2.26 other tasks" after an interruption, they framed it as distraction. But if you're a consultant switching from a legal brief to a client pitch, then checking email, then revisiting teaching notes—those might not be random. If the pitch uses different cognitive resources (visual vs. verbal), that's incubation. If the email contains a client question related to the brief, that's information gathering. The "cascade" could be an exploratory search for relevant connections.
              </p>

              <p style={{ marginBottom: '12px' }}>
                <strong style={{ color: theme.text, fontWeight: '500' }}>Productive attention residue:</strong> Sophie Leroy's research on "attention residue" showed it's costly when switching <em>away from unfinished tasks under time pressure</em>. But a 2017 study found "residual engagement"—high engagement in Task A can boost performance on Task B. For portfolio workers applying the same underlying skill (persuasive communication, systems thinking) across law, teaching, and consulting, having "residue" from the legal brief active while drafting curriculum might surface useful analogies.
              </p>

              <p style={{ marginBottom: '12px' }}>
                <strong style={{ color: theme.text, fontWeight: '500' }}>Neurodivergent advantages:</strong> ADHD adults in one study showed no impairment on multitasking performance. A Stanford study found one group "less likely to be stressed by interruption and maintained a more positive outlook"—fitting polychronic/ADHD profiles who thrive on novelty and variety in cognitively interesting work.
              </p>

              <p style={{ marginBottom: 0 }}>
                The linear-work research measured external interruptions and forced switches. What's missing: studies of workers who <em>voluntarily switch between thematically related projects</em> (lateral skill application). The "costs" of context-switching may not apply—or may flip to benefits—for this type of work.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      maxWidth: '400px',
      minHeight: '100vh',
      background: theme.bg,
      color: theme.text,
      padding: '0',
      margin: '0',
      letterSpacing: '0.01em',
    }}>
      <div style={{
        display: 'flex',
        gap: '24px',
        padding: '20px 24px 16px',
        borderBottom: `1px solid ${theme.border}`,
        background: theme.bgElevated,
      }}>
        {['Active', 'History', 'Resources', 'Support', 'Feedback', 'Settings'].map(tab => (
          <button
            key={tab}
            onClick={() => {
              setView(tab.toLowerCase());
              if (tab === 'Active') {
                setShowOnboarding(false);
              }
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: view === tab.toLowerCase() ? theme.text : theme.textTertiary,
              fontSize: '13px',
              fontWeight: view === tab.toLowerCase() ? '500' : '400',
              cursor: 'pointer',
              padding: '0',
              letterSpacing: '0.3px',
              textTransform: 'uppercase',
              transition: 'color 0.2s ease',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {view === 'active' && (
        <div>
          {activeCommitment && (
            <div style={{
              position: 'sticky',
              top: 0,
              background: theme.surface,
              borderBottom: `1px solid ${theme.border}`,
              padding: '20px 24px',
              boxShadow: `0 2px 8px ${theme.shadow}`,
              zIndex: 100,
            }}>
              <div style={{ fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', color: theme.textTertiary, marginBottom: '8px' }}>
                Current Commitment
              </div>
              <div style={{ fontSize: '15px', fontWeight: '400', color: theme.text, marginBottom: '16px', lineHeight: '1.4' }}>
                {activeCommitment.commitment}
              </div>

              <div style={{ fontSize: '28px', fontWeight: '300', color: theme.text, marginBottom: '16px', letterSpacing: '-0.5px' }}>
                {formatTime(elapsedTime)}
              </div>

              <button
                onClick={endCommitment}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: theme.accent,
                  border: 'none',
                  borderRadius: '2px',
                  color: theme.surface,
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                }}
              >
                I'm Done!
              </button>
            </div>
          )}

          {!activeCommitment && (
            <div style={{ padding: '24px' }}>
              <div style={{ fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', color: theme.textTertiary, marginBottom: '12px' }}>
                Start a Commitment
              </div>
              <input
                type="text"
                value={commitment}
                onChange={(e) => setCommitment(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && startCommitment()}
                placeholder="What are you working on?"
                style={{
                  width: '100%',
                  padding: '12px',
                  background: theme.bgElevated,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '2px',
                  fontSize: '14px',
                  color: theme.text,
                  marginBottom: '12px',
                  fontFamily: 'inherit',
                  letterSpacing: '0.01em',
                }}
              />
              <button
                onClick={startCommitment}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: theme.accent,
                  border: 'none',
                  borderRadius: '2px',
                  color: theme.surface,
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                }}
              >
                Start
              </button>
            </div>
          )}

          <div style={{ padding: '24px', borderTop: `1px solid ${theme.border}` }}>
            <div style={{ fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', color: theme.accent, marginBottom: '8px' }}>
              ♥ HeartMath Coherence
            </div>
            {!activeCoherence && !selectedTechnique ? (
              <>
                <div style={{ fontSize: '13px', color: theme.textSecondary, marginBottom: '16px', lineHeight: '1.5' }}>
                  Take a coherence break to reset your nervous system.
                </div>
                
                {/* Show total HeartMath time during this commitment */}
                {activeCommitment && totalCoherenceTime > 0 && (
                  <div style={{
                    padding: '8px 12px',
                    background: theme.bgElevated,
                    border: `1px solid ${theme.border}`,
                    borderRadius: '2px',
                    marginBottom: '12px',
                    fontSize: '11px',
                    color: theme.textSecondary,
                    textAlign: 'center'
                  }}>
                    Total coherence time: <strong style={{ color: theme.accent }}>{formatTime(totalCoherenceTime)}</strong>
                  </div>
                )}
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {Object.keys(techniqueInstructions).map(technique => (
                    <button
                      key={technique}
                      onClick={() => setSelectedTechnique(technique)}
                      style={{
                        padding: '12px',
                        background: 'transparent',
                        border: `1px solid ${theme.border}`,
                        borderRadius: '2px',
                        color: theme.text,
                        fontSize: '13px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontWeight: '400',
                      }}
                    >
                      {technique}
                    </button>
                  ))}
                </div>
              </>
            ) : !activeCoherence && selectedTechnique ? (
              <div>
                <div style={{ fontSize: '14px', fontWeight: '400', marginBottom: '12px' }}>
                  {selectedTechnique}
                </div>
                <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '12px', lineHeight: '1.5' }}>
                  {techniqueInstructions[selectedTechnique]}
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '11px', color: theme.textTertiary, marginBottom: '6px', letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                    Emotion or Intention (optional)
                  </div>
                  <input
                    type="text"
                    value={coherenceEmotion}
                    onChange={(e) => setCoherenceEmotion(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && startCoherence(selectedTechnique, coherenceEmotion)}
                    placeholder="e.g., Gratitude, Peace, Confidence..."
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: theme.bgElevated,
                      border: `1px solid ${theme.border}`,
                      borderRadius: '2px',
                      fontSize: '13px',
                      color: theme.text,
                      fontFamily: 'inherit',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => {
                      setSelectedTechnique(null);
                      setCoherenceEmotion('');
                    }}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: 'transparent',
                      border: `1px solid ${theme.border}`,
                      borderRadius: '2px',
                      color: theme.textSecondary,
                      fontSize: '11px',
                      cursor: 'pointer',
                      fontWeight: '400',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Back
                  </button>
                  <button
                    onClick={() => startCoherence(selectedTechnique, coherenceEmotion)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: theme.accent,
                      border: 'none',
                      borderRadius: '2px',
                      color: theme.surface,
                      fontSize: '11px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Start
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '15px', fontWeight: '400', marginBottom: '4px' }}>
                  {activeCoherence.technique}
                </div>
                {activeCoherence.emotion && (
                  <div style={{ fontSize: '12px', color: theme.accent, marginBottom: '8px', fontStyle: 'italic' }}>
                    {activeCoherence.emotion}
                  </div>
                )}
                
                {/* Breathing bubble container */}
                <div style={{
                  position: 'relative',
                  height: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px',
                }}>
                  {/* Timer overlay */}
                  <div style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontSize: '18px',
                    fontWeight: '300',
                    letterSpacing: '-0.3px',
                    color: theme.accent
                  }}>
                    {formatTime(coherenceElapsed)}
                  </div>

                  {/* Breathing circle */}
                  <div
                    style={{
                      width: breathPhase === 'inhale' ? '120px' : '80px',
                      height: breathPhase === 'inhale' ? '120px' : '80px',
                      borderRadius: '50%',
                      background: `radial-gradient(circle, ${theme.accent}40, ${theme.accent}10)`,
                      border: `2px solid ${theme.accent}`,
                      transition: 'all 5s cubic-bezier(0.4, 0, 0.2, 1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: theme.accent,
                      textTransform: 'capitalize',
                    }}
                  >
                    {breathPhase === 'inhale' ? 'Inhale' : 'Exhale'}
                  </div>
                </div>

                {showInstructions && (
                  <div style={{
                    padding: '12px',
                    background: `${theme.accent}10`,
                    border: `1px solid ${theme.accent}30`,
                    borderRadius: '2px',
                    fontSize: '12px',
                    lineHeight: '1.5',
                    color: theme.textSecondary,
                    marginBottom: '16px',
                    position: 'relative'
                  }}>
                    <button
                      onClick={() => setShowInstructions(false)}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: 'transparent',
                        border: 'none',
                        fontSize: '16px',
                        cursor: 'pointer',
                        color: theme.textSecondary,
                        padding: '4px',
                        lineHeight: 1
                      }}
                    >
                      ×
                    </button>
                    {techniqueInstructions[activeCoherence.technique]}
                  </div>
                )}

                <button
                  onClick={endCoherence}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: theme.accent,
                    border: 'none',
                    borderRadius: '2px',
                    color: theme.surface,
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  Complete
                </button>
              </div>
            )}
          </div>

          <div style={{ padding: '24px', borderTop: `1px solid ${theme.border}` }}>
            <div style={{ fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', color: theme.textTertiary, marginBottom: '16px' }}>
              Your Tasks
            </div>

              {workSpheres.length === 0 && (
                <div style={{
                  padding: '16px',
                  background: theme.accentSubtle,
                  border: `1px solid ${theme.accent}`,
                  borderRadius: '2px',
                  marginBottom: '16px',
                  fontSize: '12px',
                  color: theme.text,
                  lineHeight: '1.5',
                }}>
                  Create work spheres in Settings first to organize your tasks.
                </div>
              )}

              {/* Message when no work spheres */}
              {workSpheres.length === 0 && (
                <div style={{
                  padding: '16px',
                  background: theme.bgElevated,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '2px',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '13px', color: theme.textSecondary, lineHeight: '1.5', marginBottom: '12px' }}>
                    To add tasks, first create work spheres in Settings.
                  </div>
                  <button
                    onClick={() => setView('settings')}
                    style={{
                      padding: '8px 16px',
                      background: theme.accent,
                      border: 'none',
                      borderRadius: '2px',
                      color: theme.surface,
                      fontSize: '11px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Go to Settings
                  </button>
                </div>
              )}

              {workSpheres.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <input
                    type="text"
                    value={newTaskName}
                    onChange={(e) => setNewTaskName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTask()}
                    placeholder="Add a new task"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: theme.bgElevated,
                      border: `1px solid ${theme.border}`,
                      borderRadius: '2px',
                      fontSize: '13px',
                      color: theme.text,
                      fontFamily: 'inherit',
                      marginBottom: '8px',
                    }}
                  />
                  <select
                    value={newTaskSphere}
                    onChange={(e) => setNewTaskSphere(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: theme.bgElevated,
                      border: `1px solid ${theme.border}`,
                      borderRadius: '2px',
                      fontSize: '13px',
                      color: theme.text,
                      fontFamily: 'inherit',
                      marginBottom: '8px',
                    }}
                  >
                    <option value="">Select work sphere</option>
                    {workSpheres.map(sphere => (
                      <option key={sphere.id} value={sphere.id}>{sphere.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={addTask}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'transparent',
                      border: `1px solid ${theme.border}`,
                      borderRadius: '2px',
                      color: theme.text,
                      fontSize: '11px',
                      cursor: 'pointer',
                      fontWeight: '400',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Add Task
                  </button>
                </div>
              )}

              {/* Current/Running Task - Visually Separate */}
              {activeTasks.filter(t => t.status === 'running').slice(0, 1).map(task => {
                const sphere = getSphereById(task.sphereId);
                const currentSessionTime = task.currentSessionStart
                  ? Math.floor((Date.now() - task.currentSessionStart) / 1000)
                  : 0;
                
                return (
                  <div
                    key={task.id}
                    style={{
                      padding: '16px',
                      background: theme.accentSubtle,
                      border: `2px solid ${theme.accent}`,
                      borderRadius: '2px',
                      marginBottom: '20px',
                      boxShadow: `0 2px 4px ${theme.shadow}`,
                    }}
                  >
                    <div style={{ fontSize: '10px', color: theme.accent, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '8px', fontWeight: '500' }}>
                      ● Currently Working On
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {sphere && (
                          <div style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            background: sphere.color,
                            flexShrink: 0,
                          }} />
                        )}
                        <div>
                          <div style={{ fontSize: '15px', fontWeight: '500', color: theme.text, marginBottom: '4px' }}>
                            {task.name}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Current session time (larger) */}
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '11px', color: theme.textTertiary, marginBottom: '4px', letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                        Current Session
                      </div>
                      <div style={{ fontSize: '24px', fontWeight: '300', color: theme.text, letterSpacing: '-0.5px' }}>
                        {formatTime(currentSessionTime)}
                      </div>
                    </div>

                    {/* Quick check-ins */}
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '11px', color: theme.textTertiary, marginBottom: '8px', letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                        Quick check-in
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => addTaskCheckIn(task.id, true)}
                          style={{
                            flex: 1,
                            padding: '8px',
                            background: 'transparent',
                            border: `1px solid ${theme.border}`,
                            borderRadius: '2px',
                            color: theme.text,
                            fontSize: '11px',
                            cursor: 'pointer',
                            fontWeight: '400',
                            transition: 'all 0.15s ease',
                          }}
                          onMouseDown={(e) => {
                            e.target.style.background = theme.accent;
                            e.target.style.color = theme.surface;
                            e.target.style.transform = 'scale(0.95)';
                          }}
                          onMouseUp={(e) => {
                            e.target.style.background = 'transparent';
                            e.target.style.color = theme.text;
                            e.target.style.transform = 'scale(1)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'transparent';
                            e.target.style.color = theme.text;
                            e.target.style.transform = 'scale(1)';
                          }}
                        >
                          Still focused
                        </button>
                        <button
                          onClick={() => addTaskCheckIn(task.id, false)}
                          style={{
                            flex: 1,
                            padding: '8px',
                            background: 'transparent',
                            border: `1px solid ${theme.border}`,
                            borderRadius: '2px',
                            color: theme.text,
                            fontSize: '11px',
                            cursor: 'pointer',
                            fontWeight: '400',
                            transition: 'all 0.15s ease',
                          }}
                          onMouseDown={(e) => {
                            e.target.style.background = theme.accent;
                            e.target.style.color = theme.surface;
                            e.target.style.transform = 'scale(0.95)';
                          }}
                          onMouseUp={(e) => {
                            e.target.style.background = 'transparent';
                            e.target.style.color = theme.text;
                            e.target.style.transform = 'scale(1)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'transparent';
                            e.target.style.color = theme.text;
                            e.target.style.transform = 'scale(1)';
                          }}
                        >
                          Got distracted
                        </button>
                      </div>
                    </div>

                    {/* Check-in count */}
                    {task.checkIns && task.checkIns.length > 0 && (
                      <div style={{ fontSize: '11px', color: theme.textTertiary, marginBottom: '12px' }}>
                        {task.checkIns.filter(c => c.focused).length} focused · {task.checkIns.filter(c => !c.focused).length} distracted
                      </div>
                    )}

                    {/* Total accumulated time */}
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '11px', color: theme.textTertiary, marginBottom: '4px', letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                        Total Time
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '400', color: theme.text }}>
                        {formatTime(task.totalTime + currentSessionTime)}
                      </div>
                    </div>

                    {/* Where I left off note */}
                    <input
                      type="text"
                      value={pauseNotes[task.id] || ''}
                      onChange={(e) => setPauseNotes(prev => ({ ...prev, [task.id]: e.target.value }))}
                      placeholder="Where I left off (optional)"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        background: theme.bgElevated,
                        border: `1px solid ${theme.border}`,
                        borderRadius: '2px',
                        fontSize: '12px',
                        color: theme.text,
                        fontFamily: 'inherit',
                        marginBottom: '12px',
                      }}
                    />

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => pauseTask(task.id)}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: 'transparent',
                          border: `1px solid ${theme.border}`,
                          borderRadius: '2px',
                          color: theme.text,
                          fontSize: '11px',
                          cursor: 'pointer',
                          fontWeight: '400',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Pause
                      </button>
                      <button
                        onClick={() => deleteTask(task.id)}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: 'transparent',
                          border: `1px solid ${theme.border}`,
                          borderRadius: '2px',
                          color: theme.textSecondary,
                          fontSize: '11px',
                          cursor: 'pointer',
                          fontWeight: '400',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Other Tasks - Below */}
              {activeTasks.filter(t => t.status !== 'running').map(task => {
                const sphere = getSphereById(task.sphereId);
                
                return (
                  <div
                    key={task.id}
                    style={{
                      padding: '16px',
                      background: theme.surface,
                      border: `1px solid ${theme.border}`,
                      borderRadius: '2px',
                      marginBottom: '12px',
                      boxShadow: `0 1px 3px ${theme.shadow}`,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {sphere && (
                          <div style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            background: sphere.color,
                            flexShrink: 0,
                          }} />
                        )}
                        <div style={{ fontSize: '14px', fontWeight: '400', color: theme.text, flex: 1 }}>
                          {task.name}
                        </div>
                        <div style={{ fontSize: '13px', color: theme.textSecondary, fontWeight: '300' }}>
                          {formatTime(Math.floor(task.totalTime / 1000))}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => startTask(task.id)}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: 'transparent',
                          border: `1px solid ${theme.border}`,
                          borderRadius: '2px',
                          color: theme.text,
                          fontSize: '11px',
                          cursor: 'pointer',
                          fontWeight: '400',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Start
                      </button>
                      <button
                        onClick={() => deleteTask(task.id)}
                        style={{
                          padding: '10px 16px',
                          background: 'transparent',
                          border: `1px solid ${theme.border}`,
                          borderRadius: '2px',
                          color: theme.textSecondary,
                          fontSize: '11px',
                          cursor: 'pointer',
                          fontWeight: '400',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
        </div>
      )}

      {view === 'history' && (
        <div>
          <div style={{
            display: 'flex',
            gap: '16px',
            padding: '16px 24px',
            borderBottom: `1px solid ${theme.border}`,
            background: theme.bgElevated,
          }}>
            {['Sessions', 'Switching Pattern', 'Coherence'].map(subTab => (
              <button
                key={subTab}
                onClick={() => setHistorySubView(subTab.toLowerCase().replace(' ', '_'))}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: historySubView === subTab.toLowerCase().replace(' ', '_') ? theme.text : theme.textTertiary,
                  fontSize: '12px',
                  fontWeight: historySubView === subTab.toLowerCase().replace(' ', '_') ? '500' : '400',
                  cursor: 'pointer',
                  padding: '0',
                  letterSpacing: '0.3px',
                  textTransform: 'uppercase',
                }}
              >
                {subTab}
              </button>
            ))}
          </div>

          <div style={{ padding: '24px' }}>
            {historySubView === 'sessions' && (
              <>
                <div style={{ fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', color: theme.textTertiary, marginBottom: '16px' }}>
                  Commitment History
                </div>
                {sessions.length === 0 ? (
                  <div style={{ fontSize: '13px', color: theme.textSecondary, textAlign: 'center', padding: '32px 0' }}>
                    No completed commitments yet
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {sessions.slice(0, 10).map(session => (
                      <div
                        key={session.id}
                        style={{
                          padding: '16px',
                          background: theme.surface,
                          border: `1px solid ${theme.border}`,
                          borderRadius: '2px',
                          boxShadow: `0 1px 3px ${theme.shadow}`,
                        }}
                      >
                        <div style={{ fontSize: '13px', color: theme.text, marginBottom: '8px', lineHeight: '1.4' }}>
                          {session.commitment}
                        </div>
                        <div style={{ fontSize: '11px', color: theme.textTertiary }}>
                          {formatTime(session.duration)} • {session.taskSwitches?.length || 0} task switches
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {historySubView === 'switching_pattern' && (
              <>
                <div style={{ fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', color: theme.textTertiary, marginBottom: '16px' }}>
                  Task Switching Patterns
                </div>
                {sessions.length === 0 || !sessions.some(s => s.taskSwitches && s.taskSwitches.length > 0) ? (
                  <div style={{ fontSize: '13px', color: theme.textSecondary, textAlign: 'center', padding: '32px 0' }}>
                    Complete a commitment with task switches to see patterns
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                    {sessions.filter(s => s.taskSwitches && s.taskSwitches.length > 0).slice(0, 5).map(session => {
                      const switches = session.taskSwitches;
                      
                      // Create chaotic force-directed layout
                      const canvasWidth = 350;
                      const canvasHeight = Math.max(300, Math.min(500, switches.length * 35));
                      const centerX = canvasWidth / 2;
                      const centerY = canvasHeight / 2;
                      
                      // Calculate positions with spiral + chaos algorithm
                      const positions = switches.map((sw, idx) => {
                        // Spiral outward based on index
                        const spiralFactor = Math.sqrt(idx + 1);
                        const angle = idx * 2.4 + (sw.duration / 100); // golden angle-ish + duration offset
                        const radius = 40 + (spiralFactor * 15); // spiral outward
                        
                        // Add controlled chaos based on duration and position
                        const chaosX = (Math.sin(idx * 2.7 + sw.duration / 30) * 25);
                        const chaosY = (Math.cos(idx * 1.9 + sw.duration / 40) * 30);
                        
                        return {
                          x: Math.max(15, Math.min(canvasWidth - 15, centerX + Math.cos(angle) * radius + chaosX)),
                          y: Math.max(15, Math.min(canvasHeight - 15, centerY + Math.sin(angle) * radius + chaosY)),
                          task: sw
                        };
                      });
                      
                      return (
                        <div
                          key={session.id}
                          style={{
                            padding: '20px',
                            background: theme.surface,
                            border: `1px solid ${theme.border}`,
                            borderRadius: '2px',
                            boxShadow: `0 1px 3px ${theme.shadow}`,
                          }}
                        >
                          <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '16px' }}>
                            {new Date(session.startTime).toLocaleDateString()} • {formatTime(session.duration)} • {switches.length} switches
                          </div>
                          
                          {/* Chaotic connect-the-dots SVG */}
                          <svg width="100%" height={canvasHeight} style={{ marginBottom: '16px' }}>
                            {/* Draw connecting lines first */}
                            {positions.map((pos, idx) => {
                              if (idx === positions.length - 1) return null;
                              const nextPos = positions[idx + 1];
                              const sphere = getSphereById(pos.task.sphereId);
                              const nextSphere = getSphereById(nextPos.task.sphereId);
                              
                              // Line style based on sphere switching
                              const sameSphere = pos.task.sphereId === nextPos.task.sphereId;
                              const strokeWidth = sameSphere ? 1.5 : 1;
                              const strokeOpacity = sameSphere ? 0.4 : 0.2;
                              
                              return (
                                <line
                                  key={`line-${idx}`}
                                  x1={pos.x}
                                  y1={pos.y}
                                  x2={nextPos.x}
                                  y2={nextPos.y}
                                  stroke={sphere?.color || theme.accent}
                                  strokeWidth={strokeWidth}
                                  opacity={strokeOpacity}
                                />
                              );
                            })}
                            
                            {/* Draw nodes */}
                            {positions.map((pos, idx) => {
                              const sphere = getSphereById(pos.task.sphereId);
                              // Node size based on duration (longer = bigger)
                              const baseRadius = 3;
                              const durationBonus = Math.min(pos.task.duration / 180, 5); // max +5px
                              const radius = baseRadius + durationBonus;
                              
                              return (
                                <g key={`node-${idx}`}>
                                  {/* Outer glow */}
                                  <circle
                                    cx={pos.x}
                                    cy={pos.y}
                                    r={radius + 2}
                                    fill={sphere?.color || theme.accent}
                                    opacity={0.15}
                                  />
                                  {/* Main dot */}
                                  <circle
                                    cx={pos.x}
                                    cy={pos.y}
                                    r={radius}
                                    fill={sphere?.color || theme.accent}
                                  />
                                </g>
                              );
                            })}
                          </svg>
                          
                          {/* Legend */}
                          <div style={{ 
                            fontSize: '10px', 
                            color: theme.textTertiary, 
                            borderTop: `1px solid ${theme.border}`, 
                            paddingTop: '12px',
                            display: 'flex',
                            gap: '16px',
                            flexWrap: 'wrap'
                          }}>
                            {Array.from(new Set(switches.map(s => s.sphereId))).map(sphereId => {
                              const sphere = getSphereById(sphereId);
                              const switchCount = switches.filter(s => s.sphereId === sphereId).length;
                              const totalTime = switches.filter(s => s.sphereId === sphereId).reduce((sum, s) => sum + s.duration, 0);
                              return (
                                <div key={sphereId} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: sphere?.color }} />
                                  <span style={{ color: theme.textSecondary }}>{sphere?.name} • {switchCount}× • {formatTime(totalTime)}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {historySubView === 'coherence' && (
              <>
                <div style={{ fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', color: theme.textTertiary, marginBottom: '16px' }}>
                  Coherence Sessions
                </div>
                {coherenceSessions.length === 0 ? (
                  <div style={{ fontSize: '13px', color: theme.textSecondary, textAlign: 'center', padding: '32px 0' }}>
                    No coherence sessions yet
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {coherenceSessions.slice(0, 10).map(session => (
                      <div
                        key={session.startTime}
                        style={{
                          padding: '16px',
                          background: theme.surface,
                          border: `1px solid ${theme.border}`,
                          borderRadius: '2px',
                          boxShadow: `0 1px 3px ${theme.shadow}`,
                        }}
                      >
                        <div style={{ fontSize: '13px', color: theme.text, marginBottom: '4px' }}>
                          {session.technique}
                        </div>
                        <div style={{ fontSize: '11px', color: theme.textTertiary }}>
                          {formatTime(session.duration)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {view === 'settings' && (
        <div style={{ padding: '24px' }}>
          <div style={{ marginBottom: '32px' }}>
            <div style={{ fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', color: theme.textTertiary, marginBottom: '16px' }}>
              Work Spheres
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <input
                type="text"
                value={newSphereName}
                onChange={(e) => setNewSphereName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addWorkSphere()}
                placeholder="Sphere name (e.g., Client Work)"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: theme.bgElevated,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '2px',
                  fontSize: '13px',
                  color: theme.text,
                  fontFamily: 'inherit',
                  marginBottom: '8px',
                }}
              />
              
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                {DEFAULT_SPHERE_COLORS.map(color => (
                  <div
                    key={color}
                    onClick={() => setNewSphereColor(color)}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: color,
                      cursor: 'pointer',
                      border: newSphereColor === color ? `3px solid ${theme.text}` : `1px solid ${theme.border}`,
                    }}
                  />
                ))}
              </div>
              
              <button
                onClick={addWorkSphere}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'transparent',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '2px',
                  color: theme.text,
                  fontSize: '11px',
                  cursor: 'pointer',
                  fontWeight: '400',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                }}
              >
                Add Sphere
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {workSpheres.map(sphere => (
                <div
                  key={sphere.id}
                  style={{
                    padding: '12px',
                    background: theme.surface,
                    border: `1px solid ${theme.border}`,
                    borderRadius: '2px',
                  }}
                >
                  {editingSphere === sphere.id ? (
                    // Edit mode
                    <div>
                      <input
                        type="text"
                        value={editSphereName}
                        onChange={(e) => setEditSphereName(e.target.value)}
                        placeholder="Sphere name"
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          background: theme.bgElevated,
                          border: `1px solid ${theme.border}`,
                          borderRadius: '2px',
                          fontSize: '13px',
                          color: theme.text,
                          fontFamily: 'inherit',
                          marginBottom: '8px',
                        }}
                      />
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                        {DEFAULT_SPHERE_COLORS.map(color => (
                          <div
                            key={color}
                            onClick={() => setEditSphereColor(color)}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: color,
                              cursor: 'pointer',
                              border: editSphereColor === color ? `3px solid ${theme.text}` : `1px solid ${theme.border}`,
                            }}
                          />
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => updateWorkSphere(sphere.id)}
                          style={{
                            flex: 1,
                            padding: '8px',
                            background: theme.accent,
                            border: 'none',
                            borderRadius: '2px',
                            color: theme.surface,
                            fontSize: '11px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingSphere(null);
                            setEditSphereName('');
                            setEditSphereColor('');
                          }}
                          style={{
                            flex: 1,
                            padding: '8px',
                            background: 'transparent',
                            border: `1px solid ${theme.border}`,
                            borderRadius: '2px',
                            color: theme.textSecondary,
                            fontSize: '11px',
                            cursor: 'pointer',
                            fontWeight: '400',
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View mode
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: sphere.color,
                        flexShrink: 0,
                      }} />
                      <div style={{ fontSize: '13px', color: theme.text, flex: 1 }}>
                        {sphere.name}
                      </div>
                      <button
                        onClick={() => {
                          setEditingSphere(sphere.id);
                          setEditSphereName(sphere.name);
                          setEditSphereColor(sphere.color);
                        }}
                        style={{
                          padding: '6px 12px',
                          background: 'transparent',
                          border: `1px solid ${theme.border}`,
                          borderRadius: '2px',
                          color: theme.text,
                          fontSize: '10px',
                          cursor: 'pointer',
                          fontWeight: '400',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteWorkSphere(sphere.id)}
                        style={{
                          padding: '6px 12px',
                          background: 'transparent',
                          border: `1px solid ${theme.border}`,
                          borderRadius: '2px',
                          color: theme.textSecondary,
                          fontSize: '10px',
                          cursor: 'pointer',
                          fontWeight: '400',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '32px', paddingTop: '24px', borderTop: `1px solid ${theme.border}` }}>
            <div style={{ fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', color: theme.textTertiary, marginBottom: '16px' }}>
              Theme
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {Object.keys(themes).map(themeName => (
                <button
                  key={themeName}
                  onClick={() => setCurrentTheme(themeName)}
                  style={{
                    padding: '16px',
                    background: currentTheme === themeName ? theme.accent : theme.surface,
                    border: `1px solid ${currentTheme === themeName ? theme.accent : theme.border}`,
                    borderRadius: '2px',
                    color: currentTheme === themeName ? theme.surface : theme.text,
                    fontSize: '13px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontWeight: currentTheme === themeName ? '500' : '400',
                  }}
                >
                  {themeName}
                </button>
              ))}
            </div>
          </div>

          <div style={{ paddingTop: '24px', borderTop: `1px solid ${theme.border}` }}>
            <div style={{ fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', color: theme.textTertiary, marginBottom: '16px' }}>
              Data Management
            </div>
            <button
              onClick={exportData}
              style={{
                width: '100%',
                padding: '12px',
                background: 'transparent',
                border: `1px solid ${theme.border}`,
                borderRadius: '2px',
                color: theme.text,
                fontSize: '11px',
                cursor: 'pointer',
                fontWeight: '400',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                marginBottom: '8px',
              }}
            >
              Download Backup
            </button>
            <label style={{ display: 'block' }}>
              <input
                type="file"
                accept=".json"
                onChange={importData}
                style={{ display: 'none' }}
              />
              <div style={{
                width: '100%',
                padding: '12px',
                background: 'transparent',
                border: `1px solid ${theme.border}`,
                borderRadius: '2px',
                color: theme.text,
                fontSize: '11px',
                cursor: 'pointer',
                fontWeight: '400',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                textAlign: 'center',
              }}>
                Import Backup
              </div>
            </label>
          </div>
        </div>
      )}

      {view === 'support' && (
        <div style={{ padding: '24px', maxWidth: '500px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ fontSize: '32px', marginBottom: '16px' }}>☕</div>
            <div style={{ fontSize: '20px', fontWeight: '400', marginBottom: '12px', color: theme.text }}>
              Support Composure
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary }}>
              Composure is free and open. Your support helps keep it that way—no ads, no tracking, no subscriptions.
            </div>
          </div>

          <a
            href="https://buy.stripe.com/eVqbJ11K4f2ebN61PH4sE00"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block',
              width: '100%',
              padding: '16px',
              background: theme.accent,
              border: 'none',
              borderRadius: '2px',
              color: theme.surface,
              fontSize: '13px',
              fontWeight: '500',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              textAlign: 'center',
              textDecoration: 'none',
              marginBottom: '24px',
            }}
          >
            Buy us a coffee ☕
          </a>

          <div style={{
            padding: '16px',
            background: theme.bgElevated,
            border: `1px solid ${theme.border}`,
            borderRadius: '2px',
            fontSize: '13px',
            lineHeight: '1.6',
            color: theme.textSecondary,
          }}>
            <strong style={{ color: theme.text, display: 'block', marginBottom: '8px' }}>What your support enables:</strong>
            • Keeping Composure free forever<br/>
            • No ads or tracking<br/>
            • Continuous improvements<br/>
            • Building more tools for non-linear workers
          </div>
        </div>
      )}

      {view === 'feedback' && (
        <div style={{ padding: '24px', maxWidth: '500px', margin: '0 auto' }}>
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '20px', fontWeight: '400', marginBottom: '12px', color: theme.text }}>
              We'd love to hear from you
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, marginBottom: '24px' }}>
              Your feedback shapes Composure. Tell us what's working, what's not, and what you'd like to see next.
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '8px', color: theme.text }}>
              Comments & Suggestions
            </div>
            <textarea
              placeholder="What would make Composure more useful for you?"
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '12px',
                background: theme.bgElevated,
                border: `1px solid ${theme.border}`,
                borderRadius: '2px',
                fontSize: '13px',
                color: theme.text,
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
          </div>

          <div style={{
            padding: '16px',
            background: theme.accentSubtle,
            border: `1px solid ${theme.accent}`,
            borderRadius: '2px',
            marginBottom: '16px',
          }}>
            <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '8px', color: theme.text }}>
              ✉️ Join our mailing list
            </div>
            <div style={{ fontSize: '12px', lineHeight: '1.5', color: theme.textSecondary, marginBottom: '12px' }}>
              Get updates, early access to new features, and insights on non-linear work.
            </div>
            <input
              type="email"
              placeholder="your@email.com"
              style={{
                width: '100%',
                padding: '10px 12px',
                background: theme.surface,
                border: `1px solid ${theme.border}`,
                borderRadius: '2px',
                fontSize: '13px',
                color: theme.text,
                fontFamily: 'inherit',
                marginBottom: '8px',
              }}
            />
            <button
              onClick={() => alert('Thanks! We\'ll add email signup functionality soon. For now, email us at hello@getcomposure.app')}
              style={{
                width: '100%',
                padding: '10px',
                background: theme.accent,
                border: 'none',
                borderRadius: '2px',
                color: theme.surface,
                fontSize: '11px',
                cursor: 'pointer',
                fontWeight: '500',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Subscribe
            </button>
          </div>

          <div style={{
            padding: '16px',
            background: theme.bgElevated,
            border: `1px solid ${theme.border}`,
            borderRadius: '2px',
            marginBottom: '24px',
          }}>
            <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '8px', color: theme.text }}>
              🚀 Get early access to Artemis Mind
            </div>
            <div style={{ fontSize: '12px', lineHeight: '1.5', color: theme.textSecondary, marginBottom: '12px' }}>
              Our next tool for portfolio workers—private beta launching soon.
            </div>
            <input
              type="email"
              placeholder="your@email.com"
              style={{
                width: '100%',
                padding: '10px 12px',
                background: theme.surface,
                border: `1px solid ${theme.border}`,
                borderRadius: '2px',
                fontSize: '13px',
                color: theme.text,
                fontFamily: 'inherit',
                marginBottom: '8px',
              }}
            />
            <button
              onClick={() => alert('Thanks for your interest! We\'ll add beta signup functionality soon. For now, email us at artemis@getcomposure.app')}
              style={{
                width: '100%',
                padding: '10px',
                background: theme.accent,
                border: 'none',
                borderRadius: '2px',
                color: theme.surface,
                fontSize: '11px',
                cursor: 'pointer',
                fontWeight: '500',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Join Beta List
            </button>
          </div>

          <button
            onClick={() => {
              const feedback = document.querySelector('textarea').value;
              if (feedback.trim()) {
                navigator.clipboard.writeText(`Composure Feedback:\n\n${feedback}`);
                alert('Feedback copied to clipboard! Please email it to hello@getcomposure.app');
              } else {
                alert('Please add some feedback first');
              }
            }}
            style={{
              width: '100%',
              padding: '14px',
              background: theme.accent,
              border: 'none',
              borderRadius: '2px',
              color: theme.surface,
              fontSize: '12px',
              cursor: 'pointer',
              fontWeight: '500',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
            }}
          >
            Copy Feedback & Email Us
          </button>

          <div style={{
            marginTop: '32px',
            paddingTop: '24px',
            borderTop: `1px solid ${theme.border}`,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '12px', color: theme.textTertiary, marginBottom: '16px', letterSpacing: '0.3px', textTransform: 'uppercase' }}>
              Connect with us
            </div>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="https://substack.com/@composureapp" target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: theme.accent, textDecoration: 'none' }}>Substack</a>
              <a href="https://discord.gg/composure" target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: theme.accent, textDecoration: 'none' }}>Discord</a>
              <a href="https://linkedin.com/company/composureapp" target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: theme.accent, textDecoration: 'none' }}>LinkedIn</a>
              <a href="https://tiktok.com/@composureapp" target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: theme.accent, textDecoration: 'none' }}>TikTok</a>
            </div>
          </div>
        </div>
      )}

      {view === 'resources' && (
        <div style={{ padding: '24px', maxWidth: '600px' }}>
          <div style={{ marginBottom: '32px' }}>
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '12px', color: theme.text }}>
              Why the Focus Tracker Works
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, marginBottom: '12px' }}>
              The Focus Commitment Tracker leverages three evidence-based mechanisms:
            </div>
            <ul style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, paddingLeft: '20px', margin: 0 }}>
              <li style={{ marginBottom: '8px' }}>
                <strong style={{ color: theme.text, fontWeight: '500' }}>Implementation intentions:</strong> Writing down what you're working on creates a mental contract. Keeping it visible helps you redirect attention when you feel the impulse to switch—you can't unconsciously drift when the goal is staring back at you.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong style={{ color: theme.text, fontWeight: '500' }}>Time awareness:</strong> Glancing at elapsed time creates a powerful anchor. Watching minutes accumulate can motivate you to stay on task or signal when it's time for a strategic break.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong style={{ color: theme.text, fontWeight: '500' }}>Metacognitive check-ins:</strong> The "still focused?" and "got distracted" buttons build awareness of your attention patterns. Clicking reinforces progress when you're on track and helps you course-correct when you've drifted.
              </li>
            </ul>
          </div>

          <div style={{ marginBottom: '32px', paddingTop: '32px', borderTop: `1px solid ${theme.border}` }}>
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '12px', color: theme.text }}>
              The Science of HeartMath Coherence
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, marginBottom: '12px' }}>
              HeartMath techniques are built on decades of research into heart-brain coherence—a state where your heart rhythm, breathing, and nervous system synchronize. This state:
            </div>
            <ul style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, paddingLeft: '20px', marginBottom: '16px', margin: 0 }}>
              <li style={{ marginBottom: '8px' }}>
                <strong style={{ color: theme.text, fontWeight: '500' }}>Reduces cortisol:</strong> Even 5 minutes of heart-focused breathing can lower stress hormones and calm the fight-or-flight response.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong style={{ color: theme.text, fontWeight: '500' }}>Improves focus:</strong> Coherence increases alpha brain waves associated with relaxed alertness, making it easier to concentrate without strain.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong style={{ color: theme.text, fontWeight: '500' }}>Builds resilience:</strong> Regular practice strengthens your ability to self-regulate emotions, especially useful when work feels frustrating or overwhelming.
              </li>
            </ul>
            <div style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, marginBottom: '16px' }}>
              Instead of doomscrolling during wait times (like when Claude is generating a response), a 2-minute coherence session lets you reset your nervous system and return to work calmer and sharper.
            </div>
            <a
              href="https://www.heartmath.org"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: '14px',
                color: theme.accent,
                textDecoration: 'none',
                padding: '10px 16px',
                border: `1px solid ${theme.accent}`,
                borderRadius: '2px',
                marginBottom: '8px'
              }}
            >
              HeartMath Institute
              <span style={{ marginLeft: '6px' }}>→</span>
            </a>
            <div style={{ fontSize: '13px', color: theme.textSecondary }}>
              Explore research, techniques, and tools for heart-brain coherence.
            </div>
          </div>

          <div style={{ paddingTop: '32px', borderTop: `1px solid ${theme.border}`, marginBottom: '32px' }}>
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '12px', color: theme.text }}>
              How to Position Composure
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, marginBottom: '16px' }}>
              Composure works best as a persistent sidebar visible in your peripheral vision while you work. When you can see your commitment and elapsed time without switching windows, you're more likely to catch yourself before drifting off task.
            </div>
            
            {/* Mockup illustration */}
            <div style={{
              padding: '16px',
              background: theme.bgElevated,
              border: `1px solid ${theme.border}`,
              borderRadius: '2px',
              marginBottom: '16px',
            }}>
              <svg width="100%" viewBox="0 0 680 480" style={{ maxWidth: '600px', display: 'block', margin: '0 auto' }}>
                <rect x="0" y="0" width="680" height="480" fill="#e8e5df"/>
                <rect x="20" y="20" width="420" height="440" fill="#2d2d2d" rx="4"/>
                <rect x="20" y="20" width="420" height="35" fill="#1e1e1e" rx="4"/>
                <circle cx="35" cy="37" r="5" fill="#ff5f56"/>
                <circle cx="55" cy="37" r="5" fill="#ffbd2e"/>
                <circle cx="75" cy="37" r="5" fill="#27c93f"/>
                <text x="95" y="42" fontFamily="monospace" fontSize="11" fill="#999">main.py</text>
                <g fontFamily="monospace" fontSize="11">
                  <text x="35" y="75" fill="#569cd6">def</text>
                  <text x="62" y="75" fill="#dcdcaa"> analyze_data</text>
                  <text x="155" y="75" fill="#d4d4d4">(</text>
                  <text x="160" y="75" fill="#9cdcfe">dataset</text>
                  <text x="210" y="75" fill="#d4d4d4">):</text>
                  <text x="50" y="95" fill="#6a9955"># Process the dataset</text>
                  <text x="50" y="115" fill="#c586c0">for</text>
                  <text x="77" y="115" fill="#9cdcfe"> item</text>
                  <text x="112" y="115" fill="#c586c0"> in</text>
                  <text x="130" y="115" fill="#9cdcfe"> dataset</text>
                  <text x="190" y="115" fill="#d4d4d4">:</text>
                  <text x="65" y="135" fill="#dcdcaa">process</text>
                  <text x="115" y="135" fill="#d4d4d4">(</text>
                  <text x="120" y="135" fill="#9cdcfe">item</text>
                  <text x="150" y="135" fill="#d4d4d4">)</text>
                </g>
                <rect x="460" y="20" width="200" height="440" fill="#f5f2ed" rx="4"/>
                <circle cx="480" cy="45" r="8" fill="none" stroke="#c8977c" strokeWidth="1.5" opacity="0.5"/>
                <circle cx="480" cy="45" r="3" fill="#c8977c"/>
                <text x="495" y="50" fontFamily="-apple-system, sans-serif" fontSize="13" fontWeight="500" fill="#3d3d3d" letterSpacing="0.5">COMPOSURE</text>
                <rect x="470" y="70" width="180" height="90" fill="#ffffff" rx="2"/>
                <text x="480" y="85" fontFamily="-apple-system, sans-serif" fontSize="9" fill="#9d9a94" letterSpacing="0.3">CURRENT COMMITMENT</text>
                <text x="480" y="105" fontFamily="-apple-system, sans-serif" fontSize="11" fill="#3d3d3d">Finish data analysis</text>
                <text x="480" y="125" fontFamily="-apple-system, sans-serif" fontSize="11" fill="#3d3d3d">module</text>
                <text x="560" y="150" fontFamily="-apple-system, sans-serif" fontSize="20" fontWeight="300" fill="#3d3d3d">47:23</text>
                <rect x="470" y="175" width="180" height="120" fill="#ffffff" rx="2"/>
                <text x="480" y="190" fontFamily="-apple-system, sans-serif" fontSize="9" fill="#9d9a94" letterSpacing="0.3">YOUR TASKS</text>
                <circle cx="478" cy="207" r="3" fill="#c8977c"/>
                <text x="488" y="210" fontFamily="-apple-system, sans-serif" fontSize="10" fill="#3d3d3d">Write tests</text>
                <text x="630" y="210" textAnchor="end" fontFamily="-apple-system, sans-serif" fontSize="10" fill="#7a7772">12:45</text>
                <circle cx="478" cy="230" r="3" fill="#b89f8a"/>
                <text x="488" y="233" fontFamily="-apple-system, sans-serif" fontSize="10" fill="#3d3d3d">Review PR</text>
                <text x="630" y="233" textAnchor="end" fontFamily="-apple-system, sans-serif" fontSize="10" fill="#7a7772">8:15</text>
                <circle cx="478" cy="253" r="3" fill="#8b8378"/>
                <text x="488" y="256" fontFamily="-apple-system, sans-serif" fontSize="10" fill="#3d3d3d">Documentation</text>
                <text x="630" y="256" textAnchor="end" fontFamily="-apple-system, sans-serif" fontSize="10" fill="#7a7772">5:30</text>
                <rect x="478" y="268" width="30" height="12" fill="#c8977c" opacity="0.2" rx="1"/>
                <text x="493" y="277" textAnchor="middle" fontFamily="-apple-system, sans-serif" fontSize="8" fill="#c8977c">● ON</text>
                <rect x="470" y="310" width="180" height="50" fill="#ffffff" rx="2"/>
                <text x="480" y="325" fontFamily="-apple-system, sans-serif" fontSize="9" fill="#9d9a94" letterSpacing="0.3">♥ HEARTMATH</text>
                <circle cx="560" cy="340" r="12" fill="none" stroke="#c8977c" strokeWidth="2" opacity="0.4"/>
                <circle cx="560" cy="340" r="8" fill="#c8977c" opacity="0.3"/>
                <g fontFamily="-apple-system, sans-serif" fontSize="9" letterSpacing="0.3">
                  <text x="480" y="435" fill="#3d3d3d" fontWeight="500">ACTIVE</text>
                  <text x="525" y="435" fill="#9d9a94">HISTORY</text>
                  <text x="580" y="435" fill="#9d9a94">SETTINGS</text>
                </g>
                <rect x="458" y="20" width="2" height="440" fill="#3d3d3d" opacity="0.05"/>
                <path d="M 390 250 L 450 250" fill="none" stroke="#c8977c" strokeWidth="1.5" opacity="0.6"/>
                <circle cx="395" cy="250" r="3" fill="#c8977c" opacity="0.6"/>
                <text x="300" y="255" fontFamily="-apple-system, sans-serif" fontSize="11" fill="#7a7772" textAnchor="end">Keep visible</text>
                <text x="300" y="270" fontFamily="-apple-system, sans-serif" fontSize="11" fill="#7a7772" textAnchor="end">while you work</text>
              </svg>
            </div>
            
            <div style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, marginBottom: '12px' }}>
              <strong style={{ color: theme.text, fontWeight: '500' }}>How to set this up:</strong>
            </div>
            <ul style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, paddingLeft: '20px', margin: 0 }}>
              <li style={{ marginBottom: '8px' }}>
                <strong style={{ color: theme.text, fontWeight: '500' }}>Mac:</strong> Click the green window button → Choose "Tile Window to Right of Screen" → Select your work window for the left side
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong style={{ color: theme.text, fontWeight: '500' }}>Windows:</strong> Drag Composure to the right edge until it snaps → Windows will show your other windows to select for the left side
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong style={{ color: theme.text, fontWeight: '500' }}>Linux:</strong> Most window managers support tiling with Super+Left/Right arrow keys
              </li>
            </ul>
          </div>

          <div style={{ paddingTop: '32px', borderTop: `1px solid ${theme.border}` }}>
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '12px', color: theme.text }}>
              Context-Switching for Portfolio Workers
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, marginBottom: '12px' }}>
              Not all context-switching is bad. Research shows that self-initiated switches between related projects can actually boost creativity and problem-solving:
            </div>
            <ul style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, paddingLeft: '20px', marginBottom: '16px', margin: 0 }}>
              <li style={{ marginBottom: '8px' }}>
                <strong style={{ color: theme.text, fontWeight: '500' }}>Voluntary vs. forced switching:</strong> Self-initiated switches have lower cognitive costs than external interruptions (Arrington & Logan, 2005).
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong style={{ color: theme.text, fontWeight: '500' }}>Incubation effects:</strong> Stepping away from a stuck problem for 4-12 minutes improves solution rates, especially when switching to tasks using different cognitive resources.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong style={{ color: theme.text, fontWeight: '500' }}>Cross-domain insights:</strong> Portfolio workers applying the same skill across different domains often experience productive "attention residue"—insights from Project A illuminating Project B.
              </li>
            </ul>
            
            <button
              onClick={() => setShowContextSwitching(!showContextSwitching)}
              style={{
                fontSize: '14px',
                color: theme.accent,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
                marginBottom: showContextSwitching ? '16px' : 0
              }}
            >
              {showContextSwitching ? 'Read less' : 'Read more'}
            </button>

            {showContextSwitching && (
              <div style={{ fontSize: '14px', lineHeight: '1.6', color: theme.textSecondary, marginTop: '16px' }}>
                <p style={{ marginBottom: '12px' }}>
                  The "23-minute recovery time" and "2.26 task cascade" often cited in productivity research apply primarily to <strong style={{ color: theme.text }}>forced interruptions</strong> and <strong style={{ color: theme.text }}>unrelated tasks</strong>. The data looks different for portfolio workers:
                </p>
                
                <p style={{ marginBottom: '12px' }}>
                  <strong style={{ color: theme.text, fontWeight: '500' }}>The cascade might be strategic:</strong> When researchers observed people visiting "2.26 other tasks" after an interruption, they framed it as distraction. But if you're a consultant switching from a legal brief to a client pitch, then checking email, then revisiting teaching notes—those might not be random. If the pitch uses different cognitive resources (visual vs. verbal), that's incubation. If the email contains a client question related to the brief, that's information gathering. The "cascade" could be an exploratory search for relevant connections.
                </p>

                <p style={{ marginBottom: '12px' }}>
                  <strong style={{ color: theme.text, fontWeight: '500' }}>Productive attention residue:</strong> Sophie Leroy's research on "attention residue" showed it's costly when switching <em>away from unfinished tasks under time pressure</em>. But a 2017 study found "residual engagement"—high engagement in Task A can boost performance on Task B. For portfolio workers applying the same underlying skill (persuasive communication, systems thinking) across law, teaching, and consulting, having "residue" from the legal brief active while drafting curriculum might surface useful analogies.
                </p>

                <p style={{ marginBottom: '12px' }}>
                  <strong style={{ color: theme.text, fontWeight: '500' }}>Neurodivergent advantages:</strong> ADHD adults in one study showed no impairment on multitasking performance. A Stanford study found one group "less likely to be stressed by interruption and maintained a more positive outlook"—fitting polychronic/ADHD profiles who thrive on novelty and variety in cognitively interesting work.
                </p>

                <p style={{ marginBottom: 0 }}>
                  The linear-work research measured external interruptions and forced switches. What's missing: studies of workers who <em>voluntarily switch between thematically related projects</em> (lateral skill application). The "costs" of context-switching may not apply—or may flip to benefits—for this type of work.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
